import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Notification } from '@/interfaces';
import { NotificationStatus } from '@/interfaces/notification.interface';
import { notificationService } from '@/services/notificationService';
import { useAuthStore } from '@/modules/admin/stores/auth';
import { UserRoles } from '@/modules/admin/interfaces/UserRoles';

function currentUserIsStaff(): boolean {
  const roleName = useAuthStore().user?.role?.name;
  return (
    roleName === UserRoles.ADMIN ||
    roleName === UserRoles.GATEKEEPER ||
    roleName === UserRoles.SUPERVISOR
  );
}

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([]);
  const unreadCount = ref<number>(0);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const unreadNotifications = computed(() => 
    notifications.value.filter(n => n.status === 'UNREAD')
  );

  const hasUnread = computed(() => unreadCount.value > 0);

  async function fetchNotifications() {
    loading.value = true;
    error.value = null;
    try {
      // Staff see the full shared feed (reports, mentions, system events);
      // everyone else sees only notifications addressed to them.
      notifications.value = currentUserIsStaff()
        ? await notificationService.getAll()
        : await notificationService.getMyNotifications();
      await fetchUnreadCount();
    } catch (e: any) {
      error.value = e.message || 'Failed to fetch notifications';
      console.error('Error fetching notifications:', e);
    } finally {
      loading.value = false;
    }
  }

  async function fetchUnreadCount() {
    try {
      unreadCount.value = await notificationService.getUnreadCount();
    } catch (e: any) {
      console.error('Error fetching unread count:', e);
    }
  }

  async function markAsRead(id: number) {
    try {
      const updated = await notificationService.markAsRead(id);
      const index = notifications.value.findIndex(n => n.id === id);
      if (index !== -1) {
        notifications.value[index] = updated;
      }
      await fetchUnreadCount();
    } catch (e: any) {
      error.value = e.message || 'Failed to mark notification as read';
      console.error('Error marking notification as read:', e);
    }
  }

  async function markAllAsRead() {
    try {
      await notificationService.markAllAsRead();
      notifications.value.forEach(n => {
        n.status = NotificationStatus.READ;
        n.readAt = new Date().toISOString();
      });
      unreadCount.value = 0;
    } catch (e: any) {
      error.value = e.message || 'Failed to mark all as read';
      console.error('Error marking all as read:', e);
    }
  }

  async function deleteNotification(id: number) {
    try {
      await notificationService.delete(id);
      notifications.value = notifications.value.filter(n => n.id !== id);
      await fetchUnreadCount();
    } catch (e: any) {
      error.value = e.message || 'Failed to delete notification';
      console.error('Error deleting notification:', e);
    }
  }

  async function resolveCommentReport(
    id: number,
    action: 'dismiss' | 'remove',
  ) {
    await notificationService.resolveCommentReport(id, action);
    // Refetch so the report reflects its resolved state and count updates.
    await fetchNotifications();
  }

  function clearError() {
    error.value = null;
  }

  return {
    notifications,
    unreadCount,
    unreadNotifications,
    hasUnread,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    resolveCommentReport,
    clearError,
  };
});
